<script lang="ts">
  // Konto: Anmelden/Registrieren, Spitzname, Stammteam, Avatar-Baukasten, Cloud-Spielstand.
  import { onMount } from 'svelte';
  import { t } from '../i18n.ts';
  import { account, register, login, logout, updateProfile } from '../api/account.ts';
  import { cloudStatus, syncAfterLogin, push } from '../game/cloud.ts';
  import { AVATAR_OPTIONS, randomAvatar, type AvatarSpec } from '../../shared/avatar.ts';
  import Avatar from './Avatar.svelte';

  let { onBack, compact = false }: { onBack?: () => void; compact?: boolean } = $props();

  let mode = $state<'login' | 'register'>('login');
  let username = $state('');
  let password = $state('');
  let nickname = $state('');
  let error = $state('');
  let busy = $state(false);
  let avatar = $state<AvatarSpec>(randomAvatar());
  let team = $state('');
  let saved = $state('');

  onMount(() => {
    if ($account) {
      avatar = { ...$account.avatar };
      nickname = $account.nickname;
      team = $account.team;
    }
  });

  async function submit(e: Event) {
    e.preventDefault();
    error = '';
    busy = true;
    try {
      const a = mode === 'register' ? await register(username, password, nickname, avatar) : await login(username, password);
      avatar = { ...a.avatar };
      nickname = a.nickname;
      team = a.team;
      // Cloud-Stand übernehmen (neuerer gewinnt); wurde lokal etwas ersetzt, neu laden
      if (await syncAfterLogin()) location.reload();
    } catch (err) {
      error = (err as Error).message;
    } finally {
      busy = false;
    }
  }

  async function saveProfile() {
    error = '';
    try {
      await updateProfile({ nickname, avatar, team });
      saved = t('account.saved');
      setTimeout(() => (saved = ''), 2500);
    } catch (err) {
      error = (err as Error).message;
    }
  }

  const PARTS = Object.keys(AVATAR_OPTIONS) as (keyof AvatarSpec)[];
  function cycle(k: keyof AvatarSpec, d: number) {
    const n = AVATAR_OPTIONS[k];
    avatar = { ...avatar, [k]: (avatar[k] + d + n) % n };
  }
  const fmt = (n: number) => new Date(n).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
</script>

<div class="acc" class:compact data-testid="account-panel">
  {#if $account}
    <div class="who">
      <Avatar spec={avatar} size={compact ? 56 : 88} label={nickname} />
      <div>
        <b data-testid="account-name">{$account.nickname}</b>
        <small>@{$account.username}{$account.team ? ' · ' + $account.team : ''}</small>
        <small class="cloud" data-testid="cloud-status">
          {#if $cloudStatus.state === 'saving'}☁ {t('account.cloudSaving')}{:else if $cloudStatus.state === 'saved'}☁ {t('account.cloudSaved', { time: fmt($cloudStatus.at) })}{:else if $cloudStatus.state === 'error'}☁ {t('account.cloudError')}{:else}☁ {t('account.cloudOn')}{/if}
        </small>
      </div>
    </div>
    {#if !compact}
      <h3>{t('account.avatar')}</h3>
      <div class="builder">
        {#each PARTS as k}
          <div class="part">
            <button class="btn small" onclick={() => cycle(k, -1)} aria-label={t('account.prev', { part: t('account.part.' + k) })}>‹</button>
            <span>{t('account.part.' + k)}</span>
            <button class="btn small" onclick={() => cycle(k, 1)} aria-label={t('account.next', { part: t('account.part.' + k) })} data-testid={'av-' + k}>›</button>
          </div>
        {/each}
        <button class="btn small" onclick={() => (avatar = randomAvatar())} data-testid="av-random">🎲 {t('account.random')}</button>
      </div>
      <label>{t('account.nickname')}<input bind:value={nickname} maxlength="20" data-testid="acc-nickname" /></label>
      <label>{t('account.team')}<input bind:value={team} maxlength="24" placeholder={t('account.teamHint')} data-testid="acc-team" /></label>
      <div class="row">
        <button class="btn primary" onclick={saveProfile} data-testid="acc-save">{t('account.saveProfile')}</button>
        <button class="btn" onclick={() => push()} data-testid="cloud-now">☁ {t('account.cloudNow')}</button>
        <button class="btn" onclick={() => logout()} data-testid="acc-logout">{t('account.logout')}</button>
      </div>
      {#if saved}<p class="ok" role="status">{saved}</p>{/if}
    {/if}
  {:else}
    <div class="tabs" role="tablist">
      <button role="tab" class="tab" class:on={mode === 'login'} aria-selected={mode === 'login'} onclick={() => (mode = 'login')} data-testid="acc-tab-login">{t('account.login')}</button>
      <button role="tab" class="tab" class:on={mode === 'register'} aria-selected={mode === 'register'} onclick={() => (mode = 'register')} data-testid="acc-tab-register">{t('account.register')}</button>
    </div>
    <form onsubmit={submit}>
      {#if mode === 'register'}
        <div class="who">
          <Avatar spec={avatar} size={56} />
          <button type="button" class="btn small" onclick={() => (avatar = randomAvatar())}>🎲 {t('account.random')}</button>
        </div>
      {/if}
      <label>{t('account.username')}<input bind:value={username} autocomplete="username" required minlength="3" maxlength="20" data-testid="acc-username" /></label>
      <label>{t('account.password')}<input type="password" bind:value={password} autocomplete={mode === 'register' ? 'new-password' : 'current-password'} required minlength="6" data-testid="acc-password" /></label>
      {#if mode === 'register'}
        <label>{t('account.nickname')}<input bind:value={nickname} maxlength="20" data-testid="acc-nickname" /></label>
      {/if}
      <button class="btn primary" type="submit" disabled={busy} data-testid="acc-submit">{mode === 'register' ? t('account.register') : t('account.login')}</button>
      {#if error}<p class="err" role="alert" data-testid="acc-error">{error}</p>{/if}
      {#if !compact}<p class="hint">{t('account.why')}</p>{/if}
    </form>
  {/if}
  {#if onBack}<button class="btn" onclick={onBack}>{t('menu.back')}</button>{/if}
</div>

<style>
  .acc {
    display: flex;
    flex-direction: column;
    gap: 10px;
    text-align: left;
  }
  .who {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .who div {
    display: flex;
    flex-direction: column;
  }
  .who small {
    opacity: 0.8;
  }
  .cloud {
    color: var(--amber3);
  }
  h3 {
    margin: 6px 0 0;
    font-family: var(--font-display);
    color: var(--amber3);
  }
  .builder {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 6px;
  }
  .part {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    background: rgba(239, 226, 196, 0.06);
    border-radius: 8px;
    padding: 2px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.9rem;
  }
  input {
    font: inherit;
    padding: 10px;
    min-height: 44px;
    border-radius: 8px;
    border: 1px solid rgba(239, 226, 196, 0.3);
    background: rgba(12, 20, 34, 0.6);
    color: inherit;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid rgba(239, 226, 196, 0.2);
  }
  .tab {
    background: none;
    border: 0;
    color: inherit;
    font: inherit;
    padding: 8px 12px;
    min-height: 44px;
    cursor: pointer;
    opacity: 0.75;
    border-bottom: 3px solid transparent;
  }
  .tab.on {
    opacity: 1;
    font-weight: 700;
    border-color: var(--amber2);
  }
  .err {
    color: var(--bad);
    margin: 0;
  }
  .ok {
    color: var(--ok);
    margin: 0;
  }
  .hint {
    font-size: 0.85rem;
    opacity: 0.8;
    margin: 0;
  }
</style>
